import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useMutation } from "@tanstack/react-query";
import { queryKeys } from "@/queryKeys";
import { borrowerService } from "@/services/borrowers";
import { useBorrowerQuery } from "./queries";
import { Borrower, BorrowerDetail, UpdateBorrowerData } from "@/types/borrower";

interface UseBorrowerDetailReturn {
  borrower: BorrowerDetail | null;
  loading: boolean;
  updating: boolean;
  error: string | null;
  patch: (data: UpdateBorrowerData) => Promise<Borrower>;
  refresh: () => void;
}

export const useBorrowerDetail = (borrowerId: number | null): UseBorrowerDetailReturn => {
  const queryClient = useQueryClient();
  const id = borrowerId ?? null;

  const { data, isLoading, error: queryError, refetch } = useBorrowerQuery(id);

  const patchMutation = useMutation({
    mutationFn: (data: UpdateBorrowerData) => {
      if (id === null) return Promise.reject(new Error("No borrower ID"));
      return borrowerService.patch(id, data);
    },
    onSuccess: (_updated) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.borrower(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.borrowers() });
      queryClient.invalidateQueries({ queryKey: ["docusignForm", id] });
      // Contracts tab prefills merchant/business/owner fields from this cache
      // and prefers those cached values over live borrower data, so without
      // this it keeps showing pre-edit values after Application tab saves.
      if (id !== null) {
        queryClient.invalidateQueries({ queryKey: ["contractForm", id] });
        // No specific contract kind here, so invalidate the whole
        // contractStatus prefix to cover every kind cached for this borrower.
        queryClient.invalidateQueries({ queryKey: ["contractStatus", id] });
      }
    },
  });

  const patch = useCallback(
    (data: UpdateBorrowerData) => {
      if (id === null) return Promise.reject(new Error("No borrower ID"));
      return patchMutation.mutateAsync(data);
    },
    [id, patchMutation]
  );

  const refresh = useCallback(() => {
    refetch();
  }, [refetch]);

  const borrower = id !== null && data?.id === id ? data : null;
  const error = (queryError as Error)?.message ?? (patchMutation.error as Error)?.message ?? null;

  return {
    borrower,
    loading: isLoading,
    updating: patchMutation.isPending,
    error: error ?? null,
    patch,
    refresh,
  };
};
